import { Avatar, AvatarImage, Button } from '@cibli/shared';
import { Camera, Trash } from 'lucide-react';
import React, { useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CameraDialog from './CameraDialog';
import CropDialog from './CropDialog';

interface PictureCaptureProps {
  initialImage?: string | null;
  onImageCapture: (imageBase64: string | null) => void;
  size?: 'sm' | 'lg';
  showPlaceholder?: boolean;
  showSettings?: boolean;
}

export type PictureCaptureRef = {
  startCamera: () => void;
};

const PictureCapture = React.forwardRef<PictureCaptureRef, PictureCaptureProps>(
  (
    { initialImage = null, onImageCapture, size = 'lg', showPlaceholder = true, showSettings },
    ref,
  ) => {
    const { t } = useTranslation();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isCropDialogOpen, setIsCropDialogOpen] = useState(false);
    const [capturedImage, setCapturedImage] = useState<string | null>(null);
    const [previewImage, setPreviewImage] = useState<string | null>(initialImage);

    // Update previewImage when initialImage changes
    useEffect(() => {
      setPreviewImage(initialImage);
    }, [initialImage]);

    const startCamera = useCallback(() => setIsDialogOpen(true), []);
    const removePhoto = () => {
      setPreviewImage(null);
      onImageCapture(null);
    };

    // When a photo is captured, open crop dialog
    const handlePhotoCaptured = (base64: string) => {
      setCapturedImage(base64);
      setIsDialogOpen(false);
      setIsCropDialogOpen(true);
    };

    // When crop is applied, update preview and notify parent
    const handleCrop = (base64: string) => {
      setPreviewImage(base64);
      onImageCapture(base64);
      setCapturedImage(null);
      setIsCropDialogOpen(false);
    };

    useImperativeHandle(ref, () => ({ startCamera }));

    const avatarSize = size === 'sm' ? 'w-32 h-32' : 'w-64 h-64';
    const cameraIconSize = size === 'sm' ? 'h-8 w-8' : 'h-16 w-16';
    const textSize = size === 'sm' ? 'text-sm' : 'text-lg';

    return (
      <div className="contents">
        {previewImage ? (
          <div className="flex flex-col items-center">
            <div className="relative">
              <Avatar className={avatarSize}>
                <AvatarImage src={previewImage} alt="Profile" />
              </Avatar>
              <div className="absolute bottom-0 right-0 flex gap-2">
                <Button type="button" variant="destructive" size="icon" onClick={removePhoto}>
                  <Trash className="h-4 w-4" />
                </Button>
                <Button type="button" variant="secondary" size="icon" onClick={startCamera}>
                  <Camera className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        ) : showPlaceholder ? (
          <div className="flex flex-col items-center">
            <div
              className={`${avatarSize} p-4 bg-primary/10 rounded-full flex flex-col items-center justify-center cursor-pointer`}
              onClick={startCamera}
            >
              <Camera className={`${cameraIconSize} text-primary`} />
              <p className={`mt-2 ${textSize} text-primary`}>{t('CV.PersonalInfo.takePhoto')}</p>
            </div>
          </div>
        ) : null}

        <CameraDialog
          open={isDialogOpen}
          showSettings={showSettings}
          onOpenChange={setIsDialogOpen}
          onPhotoCaptured={handlePhotoCaptured}
        />

        <CropDialog
          open={isCropDialogOpen}
          onOpenChange={setIsCropDialogOpen}
          image={capturedImage}
          onCrop={handleCrop}
          onCancel={() => setIsDialogOpen(true)}
        />
      </div>
    );
  },
);

export default PictureCapture;
